import { createContext, useContext, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { decodeJwt } from 'jose';
import { CountryCode } from 'libphonenumber-js';
import { useLocalStorage } from 'react-use';
import { Hierarchy, User } from 'types/graphql';

import { navigate, routes } from '@redwoodjs/router';
import { useMutation } from '@redwoodjs/web';

import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { ACCESS_TOKEN_KEY } from 'src/constants';
import { CREATE_LOGOUT_AUDITLOG_MUTATION } from 'src/graphql/mutations/createLogoutAuditLog.mutation';
import { LOGIN_MUTATION, RESET_PASSWORD_MUTATION } from 'src/graphql/mutations/login.mutation';
import { GET_CURRENCY_BY_CODE } from 'src/graphql/queries/countries.query';
import { useCountries } from 'src/hooks';
import { handleErrors } from 'src/lib/formatters';
import { isTokenValid } from 'src/utils/auth/token';
import { CheckTestingWithCypress } from 'src/utils/test-utils';

type Country = {
    country: CountryCode;
    name: string;
    flag: string;
    callingCode: string;
};

type AuthContextType = {
    login: (email: string, password: string, resetToken?: string) => Promise<void>;
    loading: boolean;
    isAuthenticated: boolean;
    isUserLoggedIn: boolean;
    resetPassword: (password: string, resetToken: string) => Promise<void>;
    logout: () => void;
    user: User;
    setUser: (user: User) => void;
    accessToken: string;
    setStoredAccessToken: (value: string) => void;
    usersCurrency: string;
    isAdmin: boolean;
    isMerchant: boolean;
    isPartner: boolean;
    isPartnerOfPartner: boolean;
    userPartnerId: number;
    countries: Country[];
    loadingCountries: boolean;
};

const AuthContext = createContext<Partial<AuthContextType>>({});

export const useAuthContext = () => {
    return useContext(AuthContext);
};

export type AuthProviderProps = {
    initialUser?: User;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ initialUser, children }) => {
    const [loading, setLoading] = useState(false);
    const [loginMutation] = useMutation(LOGIN_MUTATION);
    const [createLogoutAuditLogMutation] = useMutation(CREATE_LOGOUT_AUDITLOG_MUTATION);
    const [resetPasswordMutation] = useMutation(RESET_PASSWORD_MUTATION);
    const [user, setUser] = useLocalStorage('user', initialUser);

    const [storedAccessToken, setStoredAccessToken] = useLocalStorage<string>(
        ACCESS_TOKEN_KEY,
        null
    );

    const [usersCurrency, setUsersCurrency] = useState<string>('');

    const accessToken = useMemo(() => {
        if (storedAccessToken) {
            return storedAccessToken;
        }
    }, [storedAccessToken]);

    const isAuthenticated: boolean = useMemo(() => {
        return isTokenValid(accessToken) && (decodeJwt(accessToken)?.otpVerified as boolean);
    }, [accessToken]);

    const { countries, loadingCountries } = useCountries(undefined, isAuthenticated);

    const isUserLoggedIn = useMemo(() => {
        return isTokenValid(accessToken);
    }, [accessToken]);

    const { isAdmin, isMerchant, isPartner, isPartnerOfPartner, userPartnerId } = useMemo(() => {
        const role: Hierarchy = user?.role?.hierarchyLevel;
        const userPartnerId: number = user?.business?.partnerId;

        return {
            isAdmin: role === 'NPI',
            isPartner: role === 'PARTNER',
            isPartnerOfPartner: role === 'PARTNER_OF_PARTNER',
            isMerchant: role === 'MERCHANT',
            userPartnerId,
        };
    }, [user]);

    useQuery(GET_CURRENCY_BY_CODE, {
        fetchPolicy: 'cache-and-network',
        variables: {
            code: user?.business?.currency,
        },
        skip: !user?.business?.currency,
        onCompleted: (data) => {
            setUsersCurrency(data.getCurrencyByCode.symbol);
        },
    });

    const login = async (username: string, password: string) => {
        try {
            setLoading(true);

            const { data } = await loginMutation({
                variables: { username, password },
            });

            localStorage.removeItem('countries');

            if (CheckTestingWithCypress() || data?.login?.user) {
                setUser(data?.login?.user);
                setStoredAccessToken(`${data?.login?.access_token}`);

                if (window.location.search.includes('redirectURI')) {
                    const params = new URLSearchParams(window.location.search);
                    const redirectURI = params.get('redirectURI');
                    navigate(redirectURI, { replace: true });
                } else {
                    navigate(routes.home(), { replace: true });
                }
            } else if (data?.login?.access_token) {
                setStoredAccessToken(`${data?.login?.access_token}`);
                if (window.location.search.includes('redirectURI')) {
                    const params = new URLSearchParams(window.location.search);
                    const redirectURI = params.get('redirectURI');
                    navigate(`/verification?redirectURI=${redirectURI}`);
                } else {
                    navigate('/verification');
                }
            }
        } catch (error) {
            showErrorToast(handleErrors(error, 'Login was unsuccessful'));
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await createLogoutAuditLogMutation();

            localStorage.clear();
            setStoredAccessToken(null);
            setUser(null);
            navigate('/login', { replace: true });
        } catch (error) {
            // do nothing
        }
    };
    const logout = async () => {
        await handleLogout();
    };

    const resetPassword = async (password: string, resetToken: string) => {
        try {
            setLoading(true);
            await resetPasswordMutation({ variables: { password, resetToken } });

            showSuccessToast('Your password has been reset! Login with your new password');
            navigate('/login');
        } catch (error) {
            showErrorToast(error.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthContext.Provider
            value={{
                login,
                loading,
                isAuthenticated,
                isUserLoggedIn,
                resetPassword,
                logout,
                user,
                setUser,
                accessToken,
                setStoredAccessToken,
                usersCurrency,
                isAdmin,
                isMerchant,
                isPartner,
                isPartnerOfPartner,
                userPartnerId,
                countries,
                loadingCountries,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
